import dayjs from 'dayjs';
import { isEmpty } from 'rambda';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SearchMessageResult } from '@/hooks/search/use-search-messages';
import ROUTES from '@/routes';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import SearchListItem from './search-list-item';

interface Props {
  results: SearchMessageResult[];
  className?: string;
  keyword?: string;
}

export default function SearchMessageResultList({ results, className, keyword = '' }: Props) {
  const { t } = useTranslation();

  if (isEmpty(results)) return null;

  return (
    <div className={cn('group/message', className)}>
      <h4 className='flex h-6 mb-2 group-first/message:mt-1 items-center px-5 text-body1 font-bold'>
        {t('search.chats')}
      </h4>

      {results.map(({ ChatSessionId, GroupId, DisplayImg, SessionName, SearchResultDetails }, i) => {
        const isGroup = Boolean(GroupId);
        const key = generateSessionKey(isGroup ? 'group' : 'session', GroupId || ChatSessionId);

        const href =
          SearchResultDetails.length > 1
            ? ROUTES.SEARCH.BY_SESSION.$buildPath({ params: { session: key }, searchParams: { keyword } })
            : isGroup
              ? ROUTES.MESSAGES.GROUPS.$buildPath({
                  params: { id: GroupId },
                  searchParams: { search: SearchResultDetails[0]?.Id },
                })
              : ROUTES.MESSAGES.SESSIONS.$buildPath({
                  params: { id: ChatSessionId },
                  searchParams: { search: SearchResultDetails[0]?.Id },
                });

        return (
          <Link
            key={`${key}-${i}`}
            to={href}>
            <SearchListItem
              avatarImageUrl={DisplayImg}
              title={SessionName}
              highlight={keyword}
              highlightField='description'
              description={
                SearchResultDetails.length > 1
                  ? t('search.related-chat-record', { count: SearchResultDetails.length })
                  : (SearchResultDetails[0]?.Message ?? '')
              }
              suffix={dayjs(SearchResultDetails[0]?.CreateTime).fromNow()}
            />
          </Link>
        );
      })}
    </div>
  );
}
